/**
 * JIRA Sync Section Component
 *
 * Displays JIRA integration status and sync controls for a project.
 * Shows sync statistics and provides bulk push functionality.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getSyncStatus,
  bulkPushRequirements,
  getProjectMappings
} from '../services/jiraService';

interface JiraSyncSectionProps {
  projectId: string;
}

export function JiraSyncSection({ projectId }: JiraSyncSectionProps) {
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [hasMapping, setHasMapping] = useState(false);
  const [syncStats, setSyncStats] = useState({
    total: 0,
    synced: 0,
    pending: 0,
    error: 0
  });
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [isExpanded, setIsExpanded] = useState(() => {
    const saved = localStorage.getItem(`projectDetail_jiraSync_expanded`);
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    loadSyncStatus();
  }, [projectId]);

  async function loadSyncStatus() {
    try {
      setLoading(true);
      setError(null);

      // Check if project has JIRA mapping
      try {
        const mappingResponse = await getProjectMappings(projectId, token!);
        setHasMapping(mappingResponse.success && mappingResponse.data);
      } catch (mappingErr: any) {
        if (mappingErr.response?.status !== 404) {
          console.error('Error loading mapping:', mappingErr);
        }
        setHasMapping(false);
      }

      // Load sync status
      try {
        const statusResponse = await getSyncStatus(projectId, token!);
        console.log('Sync status response:', statusResponse);
        if (statusResponse.success && statusResponse.data.stats) {
          console.log('Stats:', statusResponse.data.stats);
          // Handle null values from SUM() when no rows match
          const stats = statusResponse.data.stats;
          setSyncStats({
            total: stats.total || 0,
            synced: stats.synced || 0,
            pending: stats.pending || 0,
            error: stats.error || 0
          });
        }
      } catch (statusErr) {
        console.error('Error loading sync status:', statusErr);
      }
    } catch (err: any) {
      console.error('Error loading JIRA sync status:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleBulkPush() {
    try {
      setPushing(true);
      setError(null);
      setSuccess(null);

      const response = await bulkPushRequirements(
        projectId,
        { skip_synced: true },
        token!
      );

      if (response.success) {
        setSuccess(`Successfully pushed ${response.data.successful} requirement(s) to JIRA`);
        loadSyncStatus(); // Reload stats
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to push requirements');
    } finally {
      setPushing(false);
    }
  }

  function handleExpandToggle() {
    const newState = !isExpanded;
    setIsExpanded(newState);
    localStorage.setItem(`projectDetail_jiraSync_expanded`, String(newState));
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading JIRA sync status...</p>
      </div>
    );
  }

  if (!hasMapping) {
    return null; // Don't show section if project isn't mapped to JIRA
  }

  const unsyncedCount = syncStats.total - syncStats.synced;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      {/* Collapsible Header */}
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={handleExpandToggle}
      >
        <div className="flex items-center gap-2">
          <svg
            className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${
              isExpanded ? 'rotate-90' : ''
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">JIRA Requirements Sync</h2>
        </div>
        {syncStats.synced > 0 && (
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            {syncStats.synced} Synced
          </span>
        )}
      </div>

      {/* Collapsible Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          {/* Success/Error Messages */}
          {error && (
            <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 rounded-md bg-green-50 border border-green-200">
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          {/* Sync Statistics */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-50">{syncStats.total}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{syncStats.synced}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Synced</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">{unsyncedCount}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Not Synced</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{syncStats.error}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Errors</div>
            </div>
          </div>

          {/* Bulk Actions */}
          {unsyncedCount > 0 && (
            <div className="flex items-center gap-3">
              <button
                onClick={handleBulkPush}
                disabled={pushing}
                className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 disabled:opacity-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {pushing ? 'Pushing...' : `Push ${unsyncedCount} Requirement${unsyncedCount !== 1 ? 's' : ''} to JIRA`}
              </button>
            </div>
          )}

          {/* Info message */}
          <div className="mt-4 p-3 rounded-md bg-blue-50 border border-blue-200">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Note:</strong> Individual requirements can be pushed to JIRA from the Requirements page.
              Synced requirements will automatically update when changed in either system.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
